import { useState } from "react"
import { DateTime } from "luxon"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBellConcierge, faList, faPen, faPlus } from "@fortawesome/pro-light-svg-icons"
import { add, type Dinero, multiply } from "dinero.js"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import { useMatchEnvironment } from "../../../../../../environment/envHooks"
import { EnvironmentType } from "../../../../../../environment/envTypes"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import {
    fetchProductsForUser,
    FetchProductsForUserResponse,
    UserDetails,
} from "../../../../../../nonAdminUser/nonAdminUserApi"
import { MeetingServiceOrder } from "../../meetingServiceTypes"
import { ProductionOrderEdit, ProductionProductOption } from "../useProductionOrderEdit"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { ProductionAddLinePicker, ProductionMeetingDefaults } from "./ProductionAddLinePicker"
import { ProductionCardMenu, ProductionCardMenuItem } from "./ProductionCardMenu"
import { ProductionOrderLines } from "./ProductionOrderLines"

// The partial update endpoint still drops new EXTRA_SERVICE lines and zeroes re-sent ones, so until
// the backend fix lands, adding a service is only offered where it can be verified.
const ADD_EXTRA_SERVICE_ENVIRONMENTS = [EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]

const selectProductOptions = (data: FetchProductsForUserResponse): ProductionProductOption[] =>
    (data.productGroups ?? []).flatMap((group) =>
        (group.products ?? []).map((product) => ({
            productId: product.id,
            name: product.name,
            itemPrice: product.price,
            stock: product.stock,
            availability: product.orderDetails?.availability,
        })),
    )

const useStyles = makeStyles()((theme) => ({
    editContainer: {
        padding: "1rem 1rem 1rem 8.4rem",
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("md")]: {
            paddingLeft: "1rem",
        },
    },
    addButton: {
        "&.MuiButton-root": {
            height: "3rem",
            padding: ".75rem 1rem",
            borderRadius: ".625rem",
            border: `2px dashed ${theme.palette.tailwind.slate[300]}`,
            backgroundColor: "transparent",
            color: theme.palette.tailwind.blue[500],
            fontSize: ".875rem",
            fontWeight: 500,
            justifyContent: "flex-start",
        },
        "&.MuiButton-root:hover": {
            borderColor: theme.palette.tailwind.blue[500],
            backgroundColor: "transparent",
        },
        "& .MuiButton-startIcon svg": {
            fontSize: "1.125rem",
        },
    },
    totalValue: {
        alignSelf: "flex-end",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    edit: ProductionOrderEdit
    onStartEdit: (deliveryKey: string, deliveryTime: DateTime) => void
}>

export function ProductionOrderItemsSection({ order, edit, onStartEdit }: Props) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()

    // Prefills for time/location-required services: the order's own meeting times when it has them,
    // otherwise the start falls back to the delivery time (matching Create order's deferred fields).
    const deliveryDateTime = dateTimeFactory.fromApi(order.deliveryDetails.time)
    const meetingStart = dateTimeFactory.fromApi(order.meetingDetails?.startDateTime ?? "")
    const meetingEnd = dateTimeFactory.fromApi(order.meetingDetails?.endDateTime ?? "")
    const meetingDefaults: ProductionMeetingDefaults = {
        deliveryDate: deliveryDateTime,
        start: meetingStart.isValid ? meetingStart : deliveryDateTime.isValid ? deliveryDateTime : null,
        end: meetingEnd.isValid ? meetingEnd : null,
    }

    // "Add line" is a dropdown: a catalog product and a product wish are typed straight into the new line,
    // an extra service is picked from its own endpoint in the panel below.
    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null)
    const [isServicePickerOpen, setIsServicePickerOpen] = useState(false)

    const isAddExtraServiceEnabled = useMatchEnvironment(ADD_EXTRA_SERVICE_ENVIRONMENTS)

    const addMenuItems: ProductionCardMenuItem[] = [
        {
            id: "meeting-service-production-view-add-menu-catalog",
            icon: faList,
            fallback: "Product from catalog",
            onClick: () => edit.addBlankLine(),
        },
        {
            id: "meeting-service-production-view-add-menu-wish",
            icon: faPen,
            fallback: "Product wish",
            onClick: () => edit.addBlankProductWish(),
        },
        ...(isAddExtraServiceEnabled
            ? [
                  {
                      id: "meeting-service-production-view-add-menu-extra-service",
                      icon: faBellConcierge,
                      fallback: "Service",
                      onClick: () => setIsServicePickerOpen(true),
                  } satisfies ProductionCardMenuItem,
              ]
            : []),
    ]

    // Shared product catalog for every editable line's product picker (React Query dedupes the request).
    const productsQuery = useApiQuery(fetchProductsForUser, {
        queryName: UserDetails.USER_PRODUCTS_LIST,
        dependencies: {
            userId: edit.addLookup?.userId ?? 0,
            merchantId: edit.addLookup?.merchantId ?? 0,
            priceListId: edit.addLookup?.priceListId ?? 0,
            deliveryDate: edit.addLookup?.deliveryDate ?? "",
        },
        enabled: edit.isEditing && Boolean(edit.addLookup),
        select: selectProductOptions,
    })

    const totalAmount = edit.editingLines.reduce<Dinero<number> | undefined>((total, line) => {
        const amount = multiply(toDinero(line.itemPrice), line.count)
        return total ? add(total, amount) : amount
    }, undefined)
    const totalLabel = totalAmount ? moneyFactory.format(totalAmount) : ""

    const editFooter = (
        <Stack spacing=".75rem" className={classes.editContainer}>
            <LocalizedStrict id="meeting-service-production-view-add-menu-button">
                <StandardButton
                    fullWidth
                    size="small"
                    variant="outlined"
                    className={classes.addButton}
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    disabled={!edit.addLookup}
                    onClick={(event) => setAddMenuAnchor(event.currentTarget)}
                >
                    Add Product
                </StandardButton>
            </LocalizedStrict>

            <ProductionCardMenu anchorEl={addMenuAnchor} items={addMenuItems} onClose={() => setAddMenuAnchor(null)} />

            {isServicePickerOpen && edit.addLookup && (
                <ProductionAddLinePicker
                    lookup={edit.addLookup}
                    seatCount={order.deliveryDetails.sittings}
                    usedProductIds={edit.usedProductIds}
                    meetingDefaults={meetingDefaults}
                    onAddExtraService={edit.addExtraService}
                    onClose={() => setIsServicePickerOpen(false)}
                />
            )}

            {totalAmount && (
                <LocalizedStrict id="meeting-service-production-view-total-with-amount" vars={{ amount: totalLabel }}>
                    <Typography variant="body1" className={classes.totalValue}>
                        Total: {totalLabel}
                    </Typography>
                </LocalizedStrict>
            )}
        </Stack>
    )

    return (
        <Box>
            <ProductionOrderLines
                order={order}
                groups={edit.deliveryGroups}
                editingKey={edit.editingKey}
                isSaving={edit.isSaving}
                hasIncompleteLine={edit.hasIncompleteLine}
                hasOnlyExtraServices={edit.hasOnlyExtraServices}
                saveAttempted={edit.saveAttempted}
                touchedKeys={edit.touchedKeys}
                usedProductIds={edit.usedProductIds}
                onCountChange={edit.setLineCount}
                onRemove={edit.removeLine}
                onStartEdit={onStartEdit}
                onSave={edit.save}
                onCancel={edit.cancelEdit}
                productOptions={productsQuery.data ?? []}
                isProductsLoading={productsQuery.isInitialLoading}
                isEditLoading={edit.isEditLoading}
                onProductChange={edit.setLineProduct}
                onWishChange={edit.setLineWish}
                editFooter={editFooter}
            />
        </Box>
    )
}
